<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import escapable from '../../../mixins/escapable';
import Settings from './index';
import ReminderRulesSettingsLogs from '../ReminderRulesSettingsLogs';
import validationRules from '../../../mixins/validationRules';

export default {
  name: 'Wrapper',
  mixins: [
    escapable,
    validationRules,
  ],
  components: {
    ...Settings,
    ReminderRulesSettingsLogs,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    actionType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      modifiableItem: undefined,
      showLogs: false,
      valid: true,
      form: false,
    };
  },
  computed: {
    ...mapState({
      action: state => state.reminders.rules.settings.action,
      deleteAction: state => state.reminders.rules.settings.delete,
    }),
    ...mapGetters({
      exceptionTypes: 'reminders/getGlobalExceptionTypes',
    }),
    changesMade() {
      const before = JSON.stringify(this.item);
      const after = JSON.stringify(this.modifiableItem);
      return before !== after;
    },
    loading() {
      return this.action.loading || this.deleteAction.loading;
    },
  },
  methods: {
    ...mapActions({
      createSetting: 'reminders/createSetting',
      updateSetting: 'reminders/updateSetting',
      deleteSetting: 'reminders/deleteSetting',
      activateSetting: 'reminders/activateSetting',
    }),
    close() {
      if (this.loading) {
        return;
      }
      this.$emit('close');
    },
    escapeHandler() {
      if (this.showLogs) {
        return;
      }
      this.close();
    },
    create() {
      if (this.action.loading || !this.$refs.form.validate() || !this.valid) {
        return;
      }
      const setting = Object.assign({}, this.modifiableItem);
      setting.data = JSON.stringify(setting.data);
      this.createSetting(setting).then(() => {
        this.close();
      }).catch(() => {
        // Handled within actions
      });
    },
    update() {
      if (this.action.loading || !this.$refs.form.validate() || !this.valid) {
        return;
      }
      const setting = Object.assign({}, this.modifiableItem);
      setting.data = JSON.stringify(setting.data);
      this.updateSetting(setting).then(() => {
        this.close();
      }).catch(() => {
        // Handled within actions
      });
    },
    save() {
      if (this.action.loading || !this.$refs.form.validate() || !this.valid) {
        return;
      }
      if (this.actionType === 'create') {
        this.create();
      } else {
        this.update();
      }
    },
    retire() {
      if (this.action.loading) {
        return;
      }
      this.deleteSetting(this.modifiableItem).then(() => {
        this.close();
      }).catch(() => {
        // Handled within actions
      });
    },
    activate() {
      if (this.action.loading) {
        return;
      }
      this.activateSetting(this.modifiableItem).then(() => {
        this.close();
      }).catch(() => {
        // Handled within actions
      });
    },
    closeLogs() {
      this.showLogs = false;
    },
    setValid(state) {
      this.valid = state;
    },
  },
  created() {
    this.modifiableItem = Object.assign({}, this.item);
  },
};
</script>

<template>
  <v-dialog
    :value="true"
    :hide-overlay="showLogs"
    max-width="800px"
    persistent
    no-click-animation
  >
    <v-card :class="{ 'reminder-rule-setting-wrapper__hidden': showLogs }">
      <v-card-title>
        <h3 class="text-capitalize">
          {{ actionType }}
          <template v-if="actionType === 'update'">Exception: {{ item.name }}</template>
          <template v-else>Exception</template>
        </h3>
        <v-spacer/>
        <v-btn class="ma-0" icon small><v-icon @click="close">close</v-icon></v-btn>
      </v-card-title>
      <v-divider/>
      <v-card-text>
        <v-form ref="form" v-model="form">
          <v-layout row>
            <v-flex xs12>
              <v-select
                v-model="modifiableItem.type"
                :items="exceptionTypes"
                :disabled="actionType === 'update'"
                label="Type"
                box
              />
            </v-flex>
          </v-layout>

          <v-layout row>
            <v-flex xs12>
              <v-text-field
                v-model="modifiableItem.name"
                :rules="[validationRules.max(50), validationRules.required]"
                label="Name"
                counter="50"
                box
                clearable
              />
            </v-flex>
          </v-layout>

          <component
            v-if="modifiableItem.type"
            :is="modifiableItem.type"
            :data.sync="modifiableItem.data"
            @valid="setValid"
          />
        </v-form>
      </v-card-text>

      <v-divider/>

      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 mb-2 class="text-no-wrap">
            <span class="body-1 font-weight-bold">Add a comment describing your change (Optional)</span>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small>information</v-icon>
              </template>
              <span>
                  The comment will be shown in the logs for each exception
                  <br>
                  <br>
                  It can help your co-workers understand changes made
                </span>
            </v-tooltip>
          </v-flex>
          <v-flex xs12 reminder-rule-setting-wrapper__comment-box>
            <v-textarea
              v-model="modifiableItem.comment"
              :rules="[validationRules.max(100)]"
              counter="100"
              label="Comment"
              box
            />
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-divider/>

      <v-card-actions class="pa-3">
        <v-btn
          v-if="actionType === 'update'"
          :disabled="loading"
          @click="showLogs = true"
        >
          Change Log
        </v-btn>
        <v-spacer/>
        <v-btn :disabled="loading" @click="close">Cancel</v-btn>
        <template v-if="actionType === 'update'">
          <v-btn
            v-if="item.deletedAt"
            :loading="deleteAction.loading"
            :disabled="loading"
            color="info"
            @click="activate"
          >
            Activate
          </v-btn>
          <v-btn
            v-else
            :loading="deleteAction.loading"
            :disabled="loading"
            color="error"
            @click="retire"
          >
            Delete
          </v-btn>
        </template>
        <v-btn
          :loading="action.loading"
          :disabled="loading || !changesMade || !form || !valid"
          color="info"
          @click="save"
        >
          {{ actionType }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <reminder-rules-settings-logs
      v-if="showLogs"
      :item="item"
      @close="closeLogs"
    />
  </v-dialog>
</template>

<style lang="scss">
.reminder-rule-setting-wrapper {
  &__hidden {
    display: none;
  }

  &__comment {
    &-box {
      position: relative;
    }

    &-icon {
      position: absolute;
      top:0;
      right: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Reminders/ReminderRuleSettingTypes/Wrapper.vue